/**
 * useDevMode — Tri-state debug mode for System Panel and Advanced Debug (DEC-047, DEC-050)
 *
 * Three debug levels:
 * - 'off'      — No debug UI shown
 * - 'basic'    — Inline SystemPanel + DebugInfo on messages
 * - 'advanced' — Basic + slide-out AdvancedDebugPanel with WS/Server/Debug logs
 *
 * Role-based access:
 * - admin     → off / basic / advanced
 * - clinician → off / basic
 * - user      → off (cycling disabled)
 *
 * Toggle via:
 * - StatusBar apple: cycles through allowed levels
 * - Keyboard: Ctrl+Shift+D (or Cmd+Shift+D on Mac) — same cycle
 *
 * Uses Zustand for global state to avoid multiple listeners (BUG-001 fix).
 */

import { create } from 'zustand'
import { useEffect, useRef } from 'react'
import { useAuthStore } from '../stores/authStore'

const STORAGE_KEY = 'janus_debug_mode'

export type DebugLevel = 'off' | 'basic' | 'advanced'
export type UserRole = 'user' | 'clinician' | 'admin'

/**
 * Get allowed debug levels for a given role.
 */
export function getAllowedLevels(role: UserRole): DebugLevel[] {
  switch (role) {
    case 'admin':
      return ['off', 'basic', 'advanced']
    case 'clinician':
      return ['off', 'basic']
    case 'user':
    default:
      return ['off']
  }
}

// Initial value from env or localStorage (backward compat: 'true' → 'basic')
function getInitialDebugLevel(): DebugLevel {
  // Check env first
  const envDebug = import.meta.env.VITE_DEBUG_MODE === 'true'
  if (envDebug) return 'basic'

  // Check localStorage
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored === 'true') return 'basic'   // backward compat
    if (stored === 'false') return 'off'     // backward compat
    if (stored === 'basic' || stored === 'advanced') return stored
    return 'off'
  } catch {
    return 'off'
  }
}

// Global store — single source of truth
interface DevModeStore {
  debugLevel: DebugLevel
  setDebugLevel: (level: DebugLevel) => void
  cycleDebugLevel: () => void
}

const useDevModeStore = create<DevModeStore>((set, get) => ({
  debugLevel: getInitialDebugLevel(),
  setDebugLevel: (level: DebugLevel) => {
    set({ debugLevel: level })
    try {
      localStorage.setItem(STORAGE_KEY, level)
    } catch {
      // Ignore storage errors
    }
  },
  cycleDebugLevel: () => {
    const current = get().debugLevel
    const role = (useAuthStore.getState().user?.role ?? 'user') as UserRole
    const allowed = getAllowedLevels(role)

    // If only 'off' is allowed, do nothing
    if (allowed.length <= 1) return

    const idx = allowed.indexOf(current)
    const next = allowed[(idx + 1) % allowed.length]
    set({ debugLevel: next })
    try {
      localStorage.setItem(STORAGE_KEY, next)
    } catch {
      // Ignore storage errors
    }
    console.log('[DevMode] Level:', next)
  },
}))

// Keyboard listener — registered once globally
let keyboardListenerRegistered = false

function registerKeyboardListener(cycleDebugLevel: () => void) {
  if (keyboardListenerRegistered) return

  const handler = (e: KeyboardEvent) => {
    if ((e.metaKey || e.ctrlKey) && e.shiftKey && (e.key === 'D' || e.key === 'd')) {
      e.preventDefault()
      cycleDebugLevel()
    }
  }

  window.addEventListener('keydown', handler)
  keyboardListenerRegistered = true
}

export function useDevMode() {
  const { debugLevel, setDebugLevel, cycleDebugLevel } = useDevModeStore()
  const role = (useAuthStore((s) => s.user?.role) ?? 'user') as UserRole
  const initialized = useRef(false)

  // Enforce role constraints: if stored level exceeds allowed, reset to 'off'
  // Skip enforcement when user hasn't loaded yet (role defaults to 'user')
  const userLoaded = useAuthStore((s) => !!s.user)
  useEffect(() => {
    if (!userLoaded) return  // Don't reset while auth is loading
    const allowed = getAllowedLevels(role)
    if (!allowed.includes(debugLevel)) {
      setDebugLevel('off')
    }
  }, [role, debugLevel, setDebugLevel, userLoaded])

  // Register keyboard listener once
  useEffect(() => {
    if (!initialized.current) {
      registerKeyboardListener(cycleDebugLevel)
      initialized.current = true
    }
  }, [cycleDebugLevel])

  // Computed helpers for backward compat and convenience
  const isDebugEnabled = debugLevel !== 'off'       // basic or advanced
  const isAdvancedDebug = debugLevel === 'advanced'
  const canToggleDebug = getAllowedLevels(role).length > 1

  return { debugLevel, setDebugLevel, cycleDebugLevel, isDebugEnabled, isAdvancedDebug, canToggleDebug, role }
}

// Export type for use in other components
export type UseDevModeReturn = ReturnType<typeof useDevMode>
